import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ChartConfiguration, ChartData, ChartEvent } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { firstValueFrom, Subscription } from 'rxjs';
import { Azienda, AziendaService } from 'src/app/services/azienda/azienda.service';
import { StorageData, StorageService } from 'src/app/services/storage/storage.service';
import { InfoUtente } from 'src/app/services/utente/utente-web.service';
import { UtenteService } from 'src/app/services/utente/utente.service';
import { UtilityService } from 'src/app/services/utility/utility.service';

@Component({
  selector: 'app-storage-chart',
  standalone: false,

  templateUrl: './storage-chart.component.html',
  styleUrl: './storage-chart.component.scss',
})
export class StorageChartComponent implements AfterViewInit, OnDestroy {
  @ViewChildren(BaseChartDirective) charts!: QueryList<BaseChartDirective>;
  @ViewChild('doughnutChartCanvas') doughnutChart: BaseChartDirective<'doughnut'> | undefined;
  private _infoUtente: InfoUtente | undefined;
  private _subAziendaCorrente: Subscription | undefined;
  private azienda: Azienda | undefined = undefined;

  @Input()
  set infoUtente(infoUtente: InfoUtente | undefined) {
    this._infoUtente = infoUtente;
  }
  get infoUtente() {
    return this._infoUtente;
  }

  private get _idUtente() {
    return this.infoUtente?.utente.id;
  }

  @Input() tipoGrafico: 'bar' | 'doughnut' = 'doughnut';

  public spinnerDoughnutChart: boolean = false;
  private _spazioTotaleDisponibile: number = 0;
  private _spazioTotaleUtilizzato: number = 0;

  // Specifico esplicitamente le chiavi valide per la visualizzazione del grafico
  public usageTypesDoughnut: Array<'availableUsage' | 'fullUsage'> = [
    'availableUsage',
    'fullUsage',
  ];
  public usageTypesBar: Array<'availableUsage' | 'storageUsageBySelectedCompany' | 'otherUsage'> = [
    'availableUsage',
    'storageUsageBySelectedCompany',
    'otherUsage',
  ];

  /* ********************* D O U G H N U T C H A R T   C O N F I G U R A T I O N ****************/
  public doughnutChartType = 'doughnut' as const;

  /*  Dati json per mappare la legenda e valori di doughnutChartData. 
      Il campo valore viene valorizzato dal esterno  */
  public datiChartDoughnut = {
    availableUsage: {
      // disponibilità
      titolo: 'Disponibilà',
      valore: 0,
      colore: '#A5DFC6',
    },
    fullUsage: {
      titolo: 'Utilizzato',
      valore: 0,
      colore: '#FF6384',
    },
  };
  public datiChartBar = {
    availableUsage: {
      titolo: 'Disponibilà',
      valore: 0,
      colore: '#A5DFC6',
    },
    storageUsageBySelectedCompany: {
      titolo: 'Utilizzo Cloud',
      valore: 0,
      colore: '#FF6384',
    },
    otherUsage: {
      titolo: 'Altro',
      valore: 0,
      colore: '#70D4FF',
    },
  };

  // Oggetto json per mappare il grafico doughnut
  public doughnutChartData: ChartData<'doughnut'> = {
    labels: [],
    datasets: [
      {
        label: 'Gigabytes',
        data: [],
        backgroundColor: [],
        hoverOffset: 4,
      },
    ],
  };

  /* Plugin per Doughnut chart */
  public doughnutChartPlugins: any[] = [];

  /* ********************* B A R C H A R T   C O N F I G U R A T I O N ****************/
  public barChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false, // rimuove griglia
        },
        ticks: {
          display: false, // rimuove valori asse
        },
        border: {
          display: false,
        },
      },
      y: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
        border: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        align: 'center',
        labels: {
          boxWidth: 12, // riduce dimensione legenda
          font: { size: 12 },
        },
      },
    },
  };

  public barChartData: ChartData<'bar'> = {
    labels: [''],
    datasets: [],
  };

  public barChartType = 'bar' as const;

  constructor(
    private readonly utenteService: UtenteService,
    private readonly _storageService: StorageService,
    private readonly utilityService: UtilityService,
    private readonly aziendaService: AziendaService,
  ) {}

  private initialized = false;

  ngAfterViewInit(): void {
    if (!this._idUtente) {
      this.infoUtente = this.utenteService.infoUtente;
    }

    this._onChangeAzienda();
  }

  ngOnDestroy(): void {
    this._subAziendaCorrente?.unsubscribe();
  }

  // events
  public chartClicked({ event, active }: { event?: ChartEvent; active?: object[] }): void {}

  public chartHovered({ event, active }: { event?: ChartEvent; active?: object[] }): void {}

  updateChart(): void {
    const chart = this.charts.find((chart: any) => chart.chart?.config.type === this.tipoGrafico);
    chart?.update();
  }

  /**
   * Recupera i dati correnti dello spazio di archiviazione per un utente specifico.
   *
   * Questi dati potrebbero non essere aggiornati in tempo reale:
   * - Rappresentano l’ultimo aggiornamento effettuato manualmente, **oppure**
   * - L’ultimo ricalcolo eseguito dallo schedulatore backend.
   *
   * I dati verranno aggiornati al successivo lancio dello schedulatore
   * o tramite un aggiornamento manuale del grafico.
   *
   * @private
   * @returns {Promise<StorageData | undefined>}
   * - `StorageData` con i valori correnti dello storage.
   * - `undefined` se l’id utente non è presente o in caso di errore.
   */
  private async _geStorageDataApi(): Promise<StorageData | undefined> {
    if (!this._idUtente) {
      return undefined;
    }

    try {
      const storageData = await firstValueFrom(this._storageService.getStorageData(this._idUtente));
      return storageData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  /**
   * Recupera e aggiorna i dati del grafico di utilizzo dello storage.
   *
   * - Se il tipo di grafico (`tipoGrafico`) è `doughnut`, mostra lo spinner di caricamento
   *   fino al termine dell’operazione.
   * - Recupera i dati correnti tramite `_geStorageDataApi()`.
   * - Esegue il controllo sullo spazio esaurito con `ctrSpazioEsaurito()`.
   * - Aggiorna il grafico tramite `_setDatiChart()`.
   *
   * @public
   * @returns {Promise<void>} Una Promise risolta al termine dell’aggiornamento.
   */
  public async getStorageData(): Promise<void> {
    if (this.tipoGrafico === 'doughnut') {
      this.spinnerDoughnutChart = true;
    }
    const storageData = await this._geStorageDataApi();
    if (!storageData) return;

    this.ctrSpazioEsaurito(storageData);
    this._setDatiChart(storageData);
  }

  /**
   * Controlla se lo spazio di archiviazione dell’utente è esaurito e,
   * se necessario, mostra un avviso all’utente.
   *
   * Il controllo viene effettuato solo per utenti con ruoli specifici
   * definiti nella lista `ruoliDaControllare`.
   *
   * Se `esito.fullUsage` è maggiore o uguale a `esito.fullStorage` e
   * il ruolo dell’utente è tra quelli previsti, viene aperto un dialog
   * con un messaggio di avviso per contattare il fornitore.
   *
   * @param {any} storageData - Oggetto contenente i dati di utilizzo dello storage,
   */
  ctrSpazioEsaurito(storageData: any) {
    const ruoliDaControllare = [
      'utente_premium_go',
      'utente_premium',
      'professionista',
      'collaboratore',
      'collaboratore_go',
    ];

    if (
      storageData.fullUsage >= storageData.fullStorage &&
      ruoliDaControllare.includes(this.infoUtente?.utente?.ruolo?.roleName as string)
    ) {
      const descrizioneElimina =
        '<div> <p><strong>Spazio di archiviazione in esaurimento. Contatta il tuo fornitore.</strong></p>';
      this.utilityService.openDialog({
        titolo: 'Spazio di Archiviazione Esaurito',
        descrizione: descrizioneElimina,
        fontWeight: '500',
        bottoni: [
          {
            nome_btn: 'Chiudi',
            handler: () => {
              this.utilityService.closeDialog();
            },
          },
        ],
      });
    }
  }

  /**
   * Richiama l’endpoint backend per ottenere i dati aggiornati relativi allo
   * spazio di archiviazione di un utente specifico.
   *
   * > Nota: il calcolo dello spazio è un’operazione pesante (es. lettura di più collection),
   * normalmente eseguita in automatico da uno schedulatore backend.
   * Questo metodo fornisce un meccanismo manuale per forzare il ricalcolo.
   *
   * @private
   * @returns {Promise<StorageData | undefined>}
   * - Un oggetto `StorageData` con i dati aggiornati dello storage.
   * - `undefined` se l’id utente non è presente o in caso di errore.
   */
  private async _updateStorageDataApi(): Promise<StorageData | undefined> {
    if (!this._idUtente) {
      return undefined;
    }

    try {
      const storageData: StorageData = await firstValueFrom(
        this._storageService.getUpdatedStorageData(this._idUtente),
      );
      return storageData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  /**
   * Aggiorna i dati del grafico di utilizzo dello storage.
   *
   * - Se il tipo di grafico (`tipoGrafico`) è `doughnut`, viene mostrato lo spinner
   *   di caricamento fino a quando i dati non sono pronti.
   * - Se il tipo è `bar`, aggiorna direttamente il grafico a barre.
   *
   * @public
   * @returns {Promise<void>} Una Promise risolta al termine dell’aggiornamento.
   */
  public async updateStorageData(): Promise<void> {
    if (this.tipoGrafico === 'doughnut') {
      this.spinnerDoughnutChart = true;
    }
    const storageData = await this._updateStorageDataApi();
    if (!storageData) return;
    this._setDatiChart(storageData);
  }

  /**
   * Imposta i dati aggiornati sul grafico in base al tipo configurato.
   *
   * - `doughnut`: aggiorna il grafico a ciambella e disattiva lo spinner di caricamento.
   * - `bar`: aggiorna il grafico a barre.
   *
   * @private
   * @param {StorageData} storageData - I dati aggiornati dello storage da visualizzare.
   */
  private _setDatiChart(storageData: StorageData) {
    switch (this.tipoGrafico) {
      case 'doughnut':
        this._popolaDatiChartDoughnut(storageData);
        this._setDatiDoughnutChart();
        this.spinnerDoughnutChart = false;
        break;
      case 'bar':
        this._popolaDatiChartBar(storageData);
        this._setDatiBarChart();
        break;
    }
    // Aggiorna il grafico
    this.updateChart();
  }

  private _popolaDatiChartDoughnut(storageData: StorageData) {
    // Mi valorizzo i dati con il valore in bytes, per avere la possibilità di convertirli in tutte le unità di misura
    this.datiChartDoughnut.availableUsage.valore = storageData.availableUsage;
    this.datiChartDoughnut.fullUsage.valore = storageData.fullUsage;

    // Mi valorizzo il disponibile e il totale utilizzato in Gigabytes per mostrarli nel centro dell grafico
    this._spazioTotaleDisponibile = this.convertiBytesToGigabytes(storageData.fullStorage);
    this._spazioTotaleUtilizzato = this.convertiBytesToGigabytes(storageData.fullUsage);
  }

  private _popolaDatiChartBar(storageData: StorageData) {
    // Mi valorizzo i dati con il valore in bytes, per avere la possibilità di convertirli in tutte le unità di misura
    this.datiChartBar.availableUsage.valore = storageData.availableUsage;
    this.datiChartBar.storageUsageBySelectedCompany.valore =
      storageData.storageUsageBySelectedCompany;
    this.datiChartBar.otherUsage.valore =
      storageData.otherUsage + storageData.databaseUsageBySelectedCompany;
  }

  /**
   * Configura i dati e i plugin del grafico a ciambella (doughnut chart).
   *
   * Questo metodo:
   * - Imposta il plugin per la visualizzazione delle informazioni al centro del grafico,
   *   mostrando lo spazio totale utilizzato e disponibile.
   * - Pulisce e aggiorna le etichette, i valori e i colori del grafico in base ai dati presenti in `datiChart`.
   * - Converte i valori in byte in Gigabyte per una rappresentazione leggibile.
   *
   * @private
   * @returns {void} Non restituisce alcun valore; aggiorna le proprietà `doughnutChartPlugins` e `doughnutChartData`.
   */
  private _setDatiDoughnutChart() {
    // Mi creo il plugin per la visualizzazione dei dati utilizzato/disponibile nel centro del grafico
    this.doughnutChartPlugins = [
      this._returnObjectDoughnutChartPlugins(
        this._spazioTotaleUtilizzato,
        this._spazioTotaleDisponibile,
      ),
    ];

    // Inizio a settare i dati sul oggetto del grafico per la visualizzazione corretta
    if (this.datiChartDoughnut) {
      // Reset doughnutChartData per evitare duplicazioni
      this.doughnutChartData.labels = [];
      const data: number[] = [];
      const backgroundColors: string[] = [];

      // Mi valorizzo l'oggetto per la visualizzazioine ( il valore di ogni oggetto in Gigabytes )
      this.usageTypesDoughnut.forEach((usageType) => {
        const usage = this.datiChartDoughnut[usageType];
        if (usage) {
          this.doughnutChartData.labels?.push(usage.titolo);
          data.push(this.convertiBytesToGigabytes(usage.valore));
          backgroundColors.push(usage.colore);
        }
      });

      // Aggiorna i datasets e il colore del grafico
      this.doughnutChartData.datasets[0].data = data;
      this.doughnutChartData.datasets[0].backgroundColor = backgroundColors;
    }
  }

  /**
   * Configura i dati del grafico a barre (bar chart).
   *
   * Questo metodo:
   * - Inizializza il grafico con l’asse X contenente l’etichetta "Gigabytes".
   * - Filtra i tipi di utilizzo presenti in `datiChart`.
   * - Per ogni tipo di utilizzo, aggiunge un dataset con:
   *   - etichetta (`label`) corrispondente al titolo,
   *   - valore convertito da byte a Gigabyte,
   *   - colore di sfondo (`backgroundColor`) definito nei dati.
   *
   * @private
   * @returns {void} Non restituisce alcun valore; aggiorna la proprietà `barChartData`.
   */
  private _setDatiBarChart() {
    // Inizio a settare i dati sul oggetto del grafico per la visualizzazione corretta
    if (this.datiChartBar) {
      // Reset doughnutChartData per evitare duplicazioni
      this.barChartData.labels = ['Gigabytes'];
      this.barChartData.datasets = [];

      // Mi valorizzo l'oggetto per la visualizzazioine ( il valore di ogni oggetto in Gigabytes )
      this.barChartData.datasets = this.usageTypesBar
        .filter((usageType) => this.datiChartBar[usageType])
        .map((usageType) => {
          const usage = this.datiChartBar[usageType];
          return {
            label: usage.titolo,
            data: [this.convertiBytesToGigabytes(usage.valore)],
            backgroundColor: usage.colore,
          };
        });
    }
  }

  /**
   * Metodo che mi crea un oggetto plugin per il grafico Doughnut
   * Gestito in questa maniera solamente per poter inserire i valori al centro del grafico
   * @param datiDoughnutChart
   * @returns
   */
  private _returnObjectDoughnutChartPlugins(
    spazioTotaleUtilizzato: number,
    spazioTotaleDisponibile: number,
  ) {
    return {
      id: 'textCenter',
      afterDraw(chart: any) {
        const txt1 = 'Spazio Utilizzato';
        const txt2 = spazioTotaleUtilizzato + '/' + spazioTotaleDisponibile + ' Gb';

        const ctx = chart.ctx;

        // Posizioni gli elementi al centro del grafico.
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
        const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

        // Configuro il font per la visualizzazione.
        const fontSizeToUse = 14;
        ctx.font = fontSizeToUse + 'px Sora';
        ctx.fillStyle = 'black';

        // Creo il testo nella posizione centrale
        ctx.fillText(txt2, centerX, centerY - 10);
        const fontSizeToUse1 = 10;
        ctx.font = fontSizeToUse1 + 'px Sora';
        ctx.fillText(txt1, centerX, centerY + 10);
      },
    };
  }

  /**
   * Metodo che mi converte in base alla quantita di bytes nell'unita di misura corrispondente
   * @param bytes
   * @returns
   */
  formatBytes(bytes: number): string {
    if (bytes <= 0) return '0 Bytes'; // Controllo per valori negativi o zero

    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let i = Math.floor(Math.log(bytes) / Math.log(1024)); // Trova l'indice dell'unità giusta

    // Dividi i bytes per 1024 elevato all'indice trovato
    let value = bytes / Math.pow(1024, i);

    // Per i Gigabytes torno un decimale altrimenti no
    if (sizes[i] === 'GB') {
      return `${value.toFixed(1)} ${sizes[i]}`;
    } else {
      return `${value.toFixed(0)} ${sizes[i]}`;
    }
  }

  /**
   * Metodo di conversione bytes to gigabytes
   * @param bytes
   */
  convertiBytesToGigabytes(bytes: number) {
    const gigabytes: number = bytes / 1024 ** 3; // Converti byte in gigabyte
    return parseFloat(gigabytes.toFixed(1));
  }

  /**
   * Sottoscrive al BehaviorSubject `aziendaCorrente` e gestisce
   * correttamente l'esecuzione di `getStorageData()`:
   *
   * - Alla **prima emissione** (ingresso nel componente):
   *    → chiama `getStorageData()` una sola volta.
   *
   * - Alle emissioni successive:
   *    → richiama `getStorageData()` **solo se l'azienda è cambiata davvero**.
   *
   * Questa funzione evita la doppia chiamata iniziale causata sia
   * da `ngAfterViewInit()` che dalla prima emissione del BehaviorSubject.
   *
   */
  private _onChangeAzienda() {
    this._subAziendaCorrente = this.aziendaService.aziendaCorrente.subscribe((azienda) => {
      // Primo ingresso nel componente, chiamata unica
      if (!this.initialized) {
        this.initialized = true;
        this.azienda = azienda;
        this.getStorageData();
        return;
      }

      // Dopo la prima volta, chiamala SOLO se azienda è cambiata
      if (this.azienda && azienda && this.azienda.id !== azienda.id) {
        this.azienda = azienda;
        this.getStorageData();
      } else {
        // Nessun cambiamento, aggiornamento interno
        this.azienda = azienda;
      }
    });
  }
}
